import React, { useState } from 'react';
import { Store, FileCheck, Package, Printer, CheckCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { apiCall, printRawHtml } from '../utils/utils.js';

const STEPS = [
    { id: 'store',   icon: Store,       title: 'Store Details',    desc: 'Your business name and contact' },
    { id: 'tax',     icon: FileCheck,   title: 'Tax Setup',        desc: 'Configure GST / FBR / provincial tax' },
    { id: 'product', icon: Package,     title: 'First Product',    desc: 'Add one product to your catalogue' },
    { id: 'printer', icon: Printer,     title: 'Printer Test',     desc: 'Check your 80mm receipt printer' },
    { id: 'done',    icon: CheckCircle, title: "You're Ready!",    desc: 'Start accepting sales' },
];

const OnboardingWizard = ({ onComplete, currentProfile, apiCall: _apiCall, showToast }) => {
    const [step, setStep] = useState(0);
    const [store, setStore] = useState({ storeName: '', address: '', contact: '' });
    const [tax, setTax]     = useState({ tax_authority: 'FBR', tax_rate: 17, currency_symbol: 'Rs' });
    const [product, setProduct] = useState({ name: '', price_profile1: '', stock: '' });
    const [saving, setSaving]   = useState(false);
    const [productAdded, setProductAdded] = useState(false);

    const stepRef = STEPS[step];

    const saveStore = async () => {
        if (!store.storeName.trim()) return showToast('Store name is required', 'error');
        setSaving(true);
        await apiCall('saveSettings', { profile_id: currentProfile, ...store, ...tax });
        setSaving(false);
        setStep(s => s + 1);
    };

    const saveTax = async () => {
        setSaving(true);
        await apiCall('saveSettings', { profile_id: currentProfile, ...store, ...tax });
        setSaving(false);
        setStep(s => s + 1);
    };

    const addProduct = async () => {
        if (!product.name.trim() || !product.price_profile1) return showToast('Product name and price required', 'error');
        setSaving(true);
        await apiCall('addProduct', {
            id: `p_${Date.now()}`, name: product.name, price_profile1: product.price_profile1,
            price_profile2: product.price_profile1, price_profile4: product.price_profile1,
            stock: parseInt(product.stock || 0), profile_id: currentProfile, is_active: 1,
        });
        setSaving(false);
        setProductAdded(true);
        setStep(s => s + 1);
    };

    const printTest = () => {
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
        <style>@page{size:80mm auto;margin:4mm}body{font-family:'Courier New',monospace;font-size:11px;width:72mm}.c{text-align:center}.b{font-weight:bold}.hr{border-top:1px dashed #000;margin:6px 0}</style>
        </head><body>
        <div class="c b" style="font-size:16px">TEST RECEIPT</div>
        <div class="hr"></div>
        <div class="c">80mm Thermal Print Test</div>
        <div class="c">${store.storeName || 'Your Store Name'}</div>
        <div class="c">${store.address || 'Your Address'}</div>
        <div class="hr"></div>
        <table style="width:100%"><tr><td>Test Item × 1</td><td style="text-align:right">Rs 100.00</td></tr></table>
        <div class="hr"></div>
        <div class="c b">TOTAL: Rs 100.00</div>
        <div class="hr"></div>
        <div class="c" style="font-size:9px">If you can read this clearly,<br>your printer is configured correctly!</div>
        <div class="c" style="font-size:9px;margin-top:6px">${new Date().toLocaleString('en-PK')}</div>
        </body></html>`;
        printRawHtml(html);
    };

    const progressPct = (step / (STEPS.length - 1)) * 100;

    return (
        <div className="fixed inset-0 bg-[#1a1a1a]/90 backdrop-blur-sm z-[500] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-[#e1e3e5] overflow-hidden">
                {/* Progress bar */}
                <div className="h-1 bg-[#e1e3e5]">
                    <div className="h-full bg-[#008060] transition-all duration-500" style={{ width: `${progressPct}%` }}/>
                </div>

                {/* Steps pill row */}
                <div className="flex gap-1 p-4 border-b border-[#e1e3e5] overflow-x-auto">
                    {STEPS.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-1 flex-shrink-0">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                                i < step ? 'bg-[#008060] text-white' :
                                i === step ? 'bg-[#1a1a1a] text-white' :
                                'bg-[#e1e3e5] text-[#5c5f62]'}`}>
                                {i < step ? '✓' : i + 1}
                            </div>
                            <span className={`text-[10px] font-semibold hidden sm:block ${i === step ? 'text-[#1a1a1a]' : 'text-[#5c5f62]'}`}>{s.title}</span>
                            {i < STEPS.length - 1 && <ChevronRight size={12} className="text-[#c9cccf] flex-shrink-0"/>}
                        </div>
                    ))}
                </div>

                {/* Step content */}
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-[#e4fcf4] text-[#008060] flex items-center justify-center">
                            <stepRef.icon size={20}/>
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-[#1a1a1a]">{stepRef.title}</h2>
                            <p className="text-xs text-[#5c5f62]">{stepRef.desc}</p>
                        </div>
                    </div>

                    {/* Step 0: Store Details */}
                    {step === 0 && (
                        <div className="space-y-4">
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Business Name *</label>
                                <input autoFocus value={store.storeName} onChange={e=>setStore({...store,storeName:e.target.value})} placeholder="e.g. Ahmed General Store" className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none focus:border-[#008060]"/></div>
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Address</label>
                                <input value={store.address} onChange={e=>setStore({...store,address:e.target.value})} placeholder="e.g. Shop 5, Main Bazaar, Lahore" className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none focus:border-[#008060]"/></div>
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">WhatsApp / Phone</label>
                                <input value={store.contact} onChange={e=>setStore({...store,contact:e.target.value})} placeholder="e.g. 03001234567" className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none focus:border-[#008060] font-mono"/></div>
                            <button onClick={saveStore} disabled={saving} className="w-full bg-[#008060] text-white py-3 rounded-xl font-bold hover:bg-[#006e52] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                {saving ? 'Saving…' : <><span>Continue</span><ChevronRight size={16}/></>}
                            </button>
                        </div>
                    )}

                    {/* Step 1: Tax Setup */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Tax Authority</label>
                                <select value={tax.tax_authority} onChange={e=>setTax({...tax,tax_authority:e.target.value})} className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none bg-white font-semibold">
                                    {['FBR','PRA','SRB','KPRA','BRA','GST','NONE'].map(a=><option key={a}>{a}</option>)}
                                </select></div>
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Tax Rate (%)</label>
                                <input type="number" min="0" max="100" value={tax.tax_rate} onChange={e=>setTax({...tax,tax_rate:e.target.value})} placeholder="e.g. 17" className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none focus:border-[#008060] font-bold"/>
                                <p className="text-[10px] text-[#5c5f62] mt-1">0 = tax disabled. Standard FBR GST rate is 17%.</p></div>
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Currency Symbol</label>
                                <select value={tax.currency_symbol} onChange={e=>setTax({...tax,currency_symbol:e.target.value})} className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none bg-white">
                                    {['Rs','₨','PKR'].map(c=><option key={c}>{c}</option>)}
                                </select></div>
                            <div className="flex gap-3">
                                <button onClick={()=>setStep(s=>s-1)} className="flex-1 bg-[#f6f7f8] text-[#1a1a1a] py-3 rounded-xl font-bold hover:bg-[#e1e3e5] transition-colors flex items-center justify-center gap-1"><ChevronLeft size={16}/> Back</button>
                                <button onClick={saveTax} disabled={saving} className="flex-1 bg-[#008060] text-white py-3 rounded-xl font-bold hover:bg-[#006e52] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                    {saving ? 'Saving…' : <><span>Continue</span><ChevronRight size={16}/></>}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: First Product */}
                    {step === 2 && (
                        <div className="space-y-4">
                            <p className="text-xs text-[#5c5f62]">Add one product to get started. You can add more from the Products tab.</p>
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Product Name *</label>
                                <input autoFocus value={product.name} onChange={e=>setProduct({...product,name:e.target.value})} placeholder="e.g. Pepsi 500ml" className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none focus:border-[#008060]"/></div>
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Selling Price (₨) *</label>
                                <input type="number" value={product.price_profile1} onChange={e=>setProduct({...product,price_profile1:e.target.value})} placeholder="e.g. 80" className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none focus:border-[#008060] font-bold text-[#008060]"/></div>
                            <div><label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Opening Stock</label>
                                <input type="number" value={product.stock} onChange={e=>setProduct({...product,stock:e.target.value})} placeholder="e.g. 100" className="w-full p-3 border border-[#c9cccf] rounded-xl text-sm outline-none focus:border-[#008060]"/></div>
                            <div className="flex gap-3">
                                <button onClick={()=>setStep(s=>s-1)} className="flex-1 bg-[#f6f7f8] text-[#1a1a1a] py-3 rounded-xl font-bold hover:bg-[#e1e3e5] transition-colors flex items-center justify-center gap-1"><ChevronLeft size={16}/> Back</button>
                                <button onClick={addProduct} disabled={saving} className="flex-1 bg-[#008060] text-white py-3 rounded-xl font-bold hover:bg-[#006e52] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                    {saving ? 'Adding…' : <><span>Add & Continue</span><ChevronRight size={16}/></>}
                                </button>
                            </div>
                            <button onClick={()=>setStep(s=>s+1)} className="w-full text-xs text-[#5c5f62] hover:text-[#1a1a1a] underline">Skip for now</button>
                        </div>
                    )}

                    {/* Step 3: Printer Test */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <div className="bg-[#f6f7f8] rounded-xl p-4 border border-[#e1e3e5] text-sm text-[#1a1a1a]">
                                <p className="font-bold mb-2">80mm Thermal Printer Setup</p>
                                <ol className="text-xs text-[#5c5f62] space-y-1 list-decimal list-inside">
                                    <li>Connect your thermal printer via USB or Bluetooth</li>
                                    <li>Set it as your default printer in Windows/Android settings</li>
                                    <li>Click "Print Test Receipt" below</li>
                                    <li>Select your printer in the print dialog, set paper size to 80mm</li>
                                </ol>
                            </div>
                            <button onClick={printTest} className="w-full bg-[#1a1a1a] text-white py-3 rounded-xl font-bold hover:bg-black transition-colors flex items-center justify-center gap-2">
                                <Printer size={16}/> Print Test Receipt
                            </button>
                            <div className="flex gap-3">
                                <button onClick={()=>setStep(s=>s-1)} className="flex-1 bg-[#f6f7f8] text-[#1a1a1a] py-3 rounded-xl font-bold hover:bg-[#e1e3e5] transition-colors flex items-center justify-center gap-1"><ChevronLeft size={16}/> Back</button>
                                <button onClick={()=>setStep(s=>s+1)} className="flex-1 bg-[#008060] text-white py-3 rounded-xl font-bold hover:bg-[#006e52] transition-colors flex items-center justify-center gap-2">
                                    <span>Looks Good!</span><ChevronRight size={16}/>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Done */}
                    {step === 4 && (
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-[#e4fcf4] rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle size={32} className="text-[#008060]"/>
                            </div>
                            <div>
                                <p className="font-black text-xl text-[#1a1a1a]">Setup Complete!</p>
                                <p className="text-sm text-[#5c5f62] mt-1">Your POS is ready. Start making sales from the POS tab.</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-left">
                                {[
                                    ['POS Tab', 'Start making sales'],
                                    ['Products', 'Add more products'],
                                    ['Customers', 'Build your customer base'],
                                    ['Settings', 'Configure FBR, receipts, staff'],
                                ].map(([t, d]) => (
                                    <div key={t} className="bg-[#f6f7f8] rounded-lg p-3 border border-[#e1e3e5]">
                                        <p className="font-bold text-xs text-[#1a1a1a]">{t}</p>
                                        <p className="text-[10px] text-[#5c5f62]">{d}</p>
                                    </div>
                                ))}
                            </div>
                            <button onClick={onComplete} className="w-full bg-[#008060] text-white py-3 rounded-xl font-black text-base hover:bg-[#006e52] transition-colors">
                                Open POS →
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingWizard;
